import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useListParams } from "hooks/useListParams"
import { usePettyCashPaymentModes, useDeletePettyCashPaymentMode } from "hooks/usePettyCashMaster"

import PettyCashPaymentModeAddEdit from "./PettyCashPaymentModeAddEdit"

const PettyCashPaymentModeList = () => {
  document.title = "Petty Cash Payment Modes"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({ page: 1, search: "" })
  const queryParams = useMemo(() => ({ page: params.page || 1, per_page: 10, search: params.search }), [params])

  const { data: responseData, isLoading, refetch, isRefetching } = usePettyCashPaymentModes(queryParams)
  const { mutateAsync: deleteItem, isPending: deleteLoading } = useDeletePettyCashPaymentMode()

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) { setSelectedData(null); setIsEdit(false) }
  }, [modal])

  const handleAddNewClick = () => { setSelectedData(null); setIsEdit(false); setModal(true) }
  const handleEditClick = item => { setSelectedData(item); setIsEdit(true); setModal(true) }
  const handleDeleteClick = item => { setSelectedData(item); setDeleteModal(true) }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Payment mode deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch {
      toast.error("Failed to delete payment mode.")
    }
  }

  const columns = useMemo(() => [
    { header: "ID", accessorKey: "id" },
    { header: "Name", accessorKey: "name" },
    { header: "Slug", accessorKey: "slug" },
    { header: "Description", cell: ({ row }) => row.original.description || "-" },
    {
      header: "Action",
      cell: ({ row }) => (
        <ul className="list-unstyled hstack gap-1 mb-0">
          <CanView access={[ACCESS.PETTY_CASH_PAYMENT_MODE.EDIT]}>
            <li>
              <Button color="info" size="sm" onClick={() => handleEditClick(row.original)} id={`edit-pcpm-${row.original.id}`}>
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`edit-pcpm-${row.original.id}`}>Edit</UncontrolledTooltip>
            </li>
          </CanView>
          <CanView access={[ACCESS.PETTY_CASH_PAYMENT_MODE.DELETE]}>
            <li>
              <Button color="danger" size="sm" onClick={() => handleDeleteClick(row.original)} id={`delete-pcpm-${row.original.id}`}>
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`delete-pcpm-${row.original.id}`}>Delete</UncontrolledTooltip>
            </li>
          </CanView>
        </ul>
      ),
    },
  ], [])

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Petty Cash" breadcrumbItem="Payment Modes">
      <div className="d-flex align-items-center mb-3">
        <h5 className="mb-0 mt-3 card-title flex-grow-1">
          Total <Badge className="badge-soft-info">{paginationInfo?.total ?? 0}</Badge>
        </h5>
        <div className="flex-shrink-0">
          <CanView access={[ACCESS.PETTY_CASH_PAYMENT_MODE.ADD]}>
            <Button color="primary" onClick={handleAddNewClick}>Add New</Button>
          </CanView>
          <Button color="light" onClick={refetch} className="ms-1">
            <i className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`} />
          </Button>
          {hasActiveFilters && <Button color="danger" onClick={resetParams} className="ms-1">Reset Filters</Button>}
        </div>
      </div>

      <DeleteModal show={deleteModal} onDeleteClick={handleDelete} loading={deleteLoading} onCloseClick={() => setDeleteModal(false)} />

      <Row className="mb-3">
        <Col sm={4}><GlobalInputSearch value={params.search} onChange={setSearch} /></Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <PettyCashPaymentModeAddEdit modal={modal} isEdit={isEdit} editedData={selectedData} toggleModal={toggleModal} />
    </PageWrapper>
  )
}

export default PettyCashPaymentModeList
